import React, { useCallback, useEffect } from 'react';
import { Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import PropTypes from 'prop-types';

import { fetchAvailabilityEvents } from '../../../../../redux/availability/availabilityThunks';
import { DrawerContentContainer } from '../../../../AdminDrawer/AdminDrawer.styles';
import { selectSelectedEmployee } from '../../../../../redux/people/peopleSlice';
import { selectHasPrivilegedRole, selectCurrentUserId } from '../../../../../redux/slices/currentUserSlice';
import { PlusIcon } from '../../../../../assets/svg';
import Events from '../../../UserAccountPanel/components/UserAvailabilityPanel/components/Events/Events';
import {
  setSelectedEvent,
  clearSelectedEvent,
  selectAllEvents,
} from '../../../../../redux/availability/availabilitySlice';
import { StyledTitleText } from './EmployeeAvailabilityView.styles';
import { selectAvailabilityStatus } from '../../../../../redux/availability/availabilitySlice';
import { LOADING_STATES } from '../../../../../utils/constants/loadingStates';
import EmployeeInfo from '../../../../EmployeeInfo/EmployeeInfo';
import { AddEventBox } from '../../../UserAccountPanel/components/UserAvailabilityPanel/UserAvailabilityPanel.styles';

const EmployeeAvailabilityView = ({ handleOpenDrawer, drawerTypes }) => {
  const dispatch = useDispatch();
  const employee = useSelector(selectSelectedEmployee);
  const events = useSelector(selectAllEvents);
  const eventStatus = useSelector(selectAvailabilityStatus);
  const isLoading = eventStatus === LOADING_STATES.LOADING;
  const hasPrivilegedRole = useSelector(selectHasPrivilegedRole);
  const currentUserId = useSelector(selectCurrentUserId);

  // Check if viewing own availability or has privileged role
  const isOwnAvailability = employee?.id === currentUserId;
  const canEdit = isOwnAvailability || hasPrivilegedRole;

  useEffect(() => {
    dispatch(fetchAvailabilityEvents(employee?.id));
  }, [dispatch, employee]);

  const handleEditEvent = useCallback(
    (event) => {
      dispatch(setSelectedEvent(event));
      handleOpenDrawer(drawerTypes.EDIT_AVAILABILITY, event, 'Edit Availability');
    },
    [dispatch, handleOpenDrawer, drawerTypes]
  );

  const handleAddEvent = useCallback(() => {
    dispatch(clearSelectedEvent());
    handleOpenDrawer(drawerTypes.CREATE_AVAILABILITY, null, 'Add Availability');
  }, [dispatch, handleOpenDrawer, drawerTypes]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      style={{ width: '100%', height: '100%' }}
    >
      <DrawerContentContainer alignment="center">
        <EmployeeInfo employee={employee} headerType="name" />

        <StyledTitleText variant="navbar">TIMES WHEN THEY ARE UNAVAILABLE TO WORK</StyledTitleText>
        <Events events={events} onClick={handleEditEvent} isLoading={isLoading} canEdit={canEdit} />
        {canEdit && (
          <AddEventBox onClick={handleAddEvent}>
            <PlusIcon />
            <Typography variant="bodyRegular">Add Event</Typography>
          </AddEventBox>
        )}
      </DrawerContentContainer>
    </motion.div>
  );
};

EmployeeAvailabilityView.propTypes = {
  handleOpenDrawer: PropTypes.func.isRequired,
  drawerTypes: PropTypes.shape({
    EDIT_AVAILABILITY: PropTypes.string.isRequired,
    CREATE_AVAILABILITY: PropTypes.string.isRequired,
  }).isRequired,
};

export default React.memo(EmployeeAvailabilityView);
